(() => {
  const moneyFormatter = (currency) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  });

  const parseBand = (variant, moq) => {
    const title = String(variant.title || '').replace(/[–—]/g, '-').trim();
    if (/default title/i.test(title)) return { ...variant, label: `${moq}+`, min: moq, max: Infinity };
    const range = title.match(/(\d+)\s*-\s*(\d+)/);
    if (range) return { ...variant, label: variant.title, min: Number(range[1]), max: Number(range[2]) };
    const plus = title.match(/(\d+)\s*\+/);
    if (plus) return { ...variant, label: variant.title, min: Number(plus[1]), max: Infinity };
    const single = title.match(/(\d+)/);
    return { ...variant, label: variant.title, min: single ? Number(single[1]) : moq, max: Infinity };
  };

  const parseManualBands = (source, moq) => String(source || '')
    .split(/\r?\n/)
    .map((line, index) => {
      const [title, amount] = line.split('|').map((value) => String(value || '').trim());
      const price = Math.round(Number(String(amount || '').replace(/[^0-9.-]/g, '')) * 100);
      if (!title || !Number.isFinite(price)) return null;
      return parseBand({ id: `manual-${index}`, title, price, available: true }, moq);
    })
    .filter(Boolean);

  function initMenu(root) {
    const button = root.querySelector('[data-nn-menu-toggle]');
    const nav = root.querySelector('[data-nn-kit-nav]');
    if (!button || !nav) return;
    root.classList.add('is-interactive');
    document.body.classList.add('nn-club-kits-is-interactive');
    const showPage = (target) => {
      const page = document.querySelector(target);
      if (!page || !page.matches('[data-nn-club-kits-page]')) return false;
      document.querySelectorAll('[data-nn-club-kits-page]').forEach((item) => item.classList.toggle('is-active', item === page));
      nav.querySelectorAll('a').forEach((item) => item.setAttribute('aria-current', String(item.getAttribute('href') === target ? 'page' : 'false')));
      return true;
    };
    window.NNClubKitsShowPage = showPage;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      nav.classList.toggle('is-open', !open);
    });
    nav.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      button.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
    });
    if (!document.documentElement.dataset.nnClubKitsNavigation) {
      document.documentElement.dataset.nnClubKitsNavigation = 'true';
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#nn-club-kits-"]');
        if (!link) return;
        const target = link.getAttribute('href');
        if (!window.NNClubKitsShowPage?.(target)) return;
        event.preventDefault();
        history.replaceState(null, '', target);
        document.querySelector('[data-nn-club-kits]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      window.addEventListener('hashchange', () => window.NNClubKitsShowPage?.(location.hash));
      document.addEventListener('shopify:section:select', (event) => {
        const page = event.target.querySelector?.('[data-nn-club-kits-page]') || (event.target.matches?.('[data-nn-club-kits-page]') ? event.target : null);
        if (page?.id) window.NNClubKitsShowPage?.(`#${page.id}`);
      });
    }
    if (location.hash && !showPage(location.hash)) showPage('#nn-club-kits-home');
  }

  function initPricing(root) {
    const pricing = root.querySelector('[data-nn-pricing]');
    const configNode = root.querySelector('[data-nn-pricing-config]');
    if (!pricing || !configNode || pricing.dataset.nnReady === 'true') return;
    pricing.dataset.nnReady = 'true';

    let config;
    try { config = JSON.parse(configNode.textContent); } catch (error) {
      console.error('Club Kits pricing configuration is invalid.', error);
      return;
    }

    const products = (config.products || []).map((product) => {
      const productBands = (product.variants || []).map((variant) => parseBand(variant, product.moq));
      return {
        ...product,
        key: `${product.familyKey}.${product.productKey}`,
        bands: (productBands.length ? productBands : parseManualBands(product.manualBands, product.moq)).sort((a, b) => a.min - b.min)
      };
    }).filter((product) => product.bands.length);
    const extras = (config.extras || []).map((extra) => ({
      ...extra,
      appliesTo: (extra.appliesTo || []).map((item) => String(item).trim()).filter(Boolean)
    }));
    const crests = (config.crests || []).length ? config.crests : [{ key: 'included', label: 'Sublimated — included', price: 0 }];
    const format = moneyFormatter(config.currency || 'CAD');
    const money = (cents) => format.format((Number(cents) || 0) / 100);
    const state = { productIndex: 0, quantity: 36, goalkeeper: 0, extras: {}, crest: crests[0].key, designs: [] };
    const find = (name) => pricing.querySelector(`[data-nn-${name}]`);
    const product = () => products[state.productIndex];
    const bandFor = (item, quantity) => item.bands.filter((band) => quantity >= band.min).pop() || null;
    const applies = (extra, item) => !extra.appliesTo.length || extra.appliesTo.includes(item.key) || extra.appliesTo.includes(item.familyKey);
    const selectedExtras = (item) => extras.filter((extra) => applies(extra, item) && !extra.included && state.extras[extra.key]);
    const crest = () => crests.find((item) => item.key === state.crest) || crests[0];
    const unitPrice = (item, quantity) => {
      const band = bandFor(item, quantity);
      if (!band) return null;
      return band.price + selectedExtras(item).reduce((sum, extra) => sum + extra.price, 0) + (crest().price || 0);
    };

    function setQuantity(value) {
      state.quantity = Math.max(1, Math.min(2000, Number.parseInt(value, 10) || 1));
      find('quantity-range').max = Math.max(500, state.quantity);
      find('quantity-range').value = state.quantity;
      find('quantity-number').value = state.quantity;
      renderPricing();
    }

    function chooseProduct(index) {
      state.productIndex = index;
      state.goalkeeper = 0;
      find('gk-range').value = 0;
      find('gk-number').value = 0;
      const item = product();
      if (item && state.quantity < item.moq) state.quantity = item.moq;
      renderAll();
    }

    function renderTabs() {
      const familyTabs = find('family-tabs');
      const productTabs = find('product-tabs');
      familyTabs.innerHTML = '';
      productTabs.innerHTML = '';
      if (!products.length) return;
      const families = [];
      products.forEach((item) => {
        if (!families.some((family) => family.key === item.familyKey)) families.push({ key: item.familyKey, label: item.familyLabel });
      });
      const current = product();
      families.forEach((family) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = family.label;
        button.setAttribute('aria-pressed', String(family.key === current.familyKey));
        button.addEventListener('click', () => chooseProduct(products.findIndex((item) => item.familyKey === family.key)));
        familyTabs.appendChild(button);
      });
      const sameFamily = products.map((item, index) => ({ item, index })).filter(({ item }) => item.familyKey === current.familyKey);
      productTabs.hidden = sameFamily.length < 2;
      sameFamily.forEach(({ item, index }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = item.productLabel;
        button.setAttribute('aria-pressed', String(index === state.productIndex));
        button.addEventListener('click', () => chooseProduct(index));
        productTabs.appendChild(button);
      });
    }

    function renderLadder() {
      const item = product();
      if (!item) return;
      const active = bandFor(item, state.quantity);
      const activeIndex = item.bands.indexOf(active);
      const ladder = find('ladder');
      ladder.innerHTML = '';
      item.bands.forEach((band, index) => {
        const segment = document.createElement('span');
        segment.className = `nn-club-kits__ladder-segment${index <= activeIndex ? ' is-reached' : ''}`;
        segment.textContent = band.label;
        ladder.appendChild(segment);
      });
      const note = find('ladder-note');
      note.className = 'nn-club-kits__ladder-note';
      note.innerHTML = '<p></p>';
      const message = note.querySelector('p');
      const addButton = (label, quantity) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.addEventListener('click', () => setQuantity(quantity));
        note.appendChild(button);
      };
      if (state.quantity < item.moq) {
        message.textContent = `Minimum is ${item.moq} per design — add ${item.moq - state.quantity} more to begin.`;
        addButton(`Set to ${item.moq}`, item.moq);
      } else {
        note.classList.add('is-earned');
        const nextBand = item.bands[activeIndex + 1];
        if (nextBand) {
          message.textContent = `${nextBand.min - state.quantity} more and every unit moves to ${money(nextBand.price)}.`;
          addButton(`Make it ${nextBand.min}`, nextBand.min);
        } else message.textContent = 'This is the best available price band for this product.';
      }
      const bands = find('bands');
      bands.innerHTML = '';
      item.bands.forEach((band) => {
        const card = document.createElement('div');
        card.className = `nn-club-kits__band${band === active ? ' is-active' : ''}`;
        card.innerHTML = `<span>${band.label}</span><b>${money(band.price)}</b>`;
        bands.appendChild(card);
      });
      find('moq').textContent = `MOQ ${item.moq} per design`;
    }

    function renderOptions() {
      const item = product();
      if (!item) return;
      const wrap = find('extras');
      wrap.innerHTML = '<h4>Options</h4>';
      extras.filter((extra) => applies(extra, item)).forEach((extra) => {
        const label = document.createElement('label');
        label.className = 'nn-club-kits__extra';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = extra.locked || Boolean(state.extras[extra.key]);
        checkbox.disabled = extra.locked;
        checkbox.addEventListener('change', () => { state.extras[extra.key] = checkbox.checked; renderPricing(); });
        const text = document.createElement('span');
        text.innerHTML = `<b>${extra.label}</b>${extra.note ? `<small>${extra.note}</small>` : ''}`;
        const price = document.createElement('span');
        price.className = 'nn-club-kits__extra-price';
        price.textContent = extra.included ? 'Included' : `+${money(extra.price)} / unit`;
        label.append(checkbox, text, price);
        wrap.appendChild(label);
      });
      const crestSelect = find('crest');
      crestSelect.innerHTML = '';
      crests.forEach((option) => {
        const element = document.createElement('option');
        element.value = option.key;
        element.textContent = option.price ? `${option.label} · +${money(option.price)} / unit` : option.label;
        element.selected = option.key === state.crest;
        crestSelect.appendChild(element);
      });
      find('goalkeeper').hidden = !item.goalkeeper;
    }

    function renderPricing() {
      const item = product();
      if (!item) return;
      renderLadder();
      const add = find('add-design');
      const price = unitPrice(item, state.quantity);
      add.disabled = state.quantity < item.moq || price === null;
      add.textContent = add.disabled ? `Minimum ${item.moq} per design` : `Add to estimate — ${money(price)} / unit`;
    }

    function renderCatalogue() {
      const catalogue = find('catalogue');
      catalogue.innerHTML = '';
      products.forEach((item) => {
        const prices = item.bands.map((band) => band.price);
        const low = Math.min(...prices);
        const high = Math.max(...prices);
        const card = document.createElement('article');
        card.className = 'nn-club-kits__catalogue-item';
        card.innerHTML = `<h4>${item.familyLabel === item.productLabel ? item.productLabel : `${item.familyLabel} — ${item.productLabel}`}</h4><b>${low === high ? money(low) : `${money(low)}–${money(high)}`}</b><p>${item.contents || item.description || ''}</p><small>Minimum ${item.moq} per design</small>`;
        catalogue.appendChild(card);
      });
    }

    function quoteSummary(total, units) {
      const lines = state.designs.map((line) => `${line.label}: ${line.quantity} × ${money(line.unitPrice)} = ${money(line.quantity * line.unitPrice)}`);
      lines.push(`Total units: ${units}`, `Estimated subtotal: ${money(total)}`, 'Tax, shipping, duty and brokerage are excluded.');
      return lines.join('\n');
    }

    function renderQuote() {
      const lines = find('quote-lines');
      if (!state.designs.length) {
        lines.innerHTML = '<p class="nn-club-kits__quote-empty">Nothing added yet. Configure a design and add it here.</p>';
        ['total', 'deposit', 'balance'].forEach((key) => { find(key).textContent = '—'; });
        find('per-unit').textContent = '—';
        find('quote-warning').textContent = '';
        sessionStorage.removeItem('nnClubKitsEstimate');
        return;
      }
      lines.innerHTML = '';
      let productTotal = 0;
      let units = 0;
      state.designs.forEach((line, index) => {
        const subtotal = line.quantity * line.unitPrice;
        productTotal += subtotal;
        units += line.quantity;
        const element = document.createElement('div');
        element.className = 'nn-club-kits__quote-line';
        element.innerHTML = `<span><b>${line.label}</b><small>${line.details}</small></span><strong>${money(subtotal)}</strong>`;
        const remove = document.createElement('button');
        remove.className = 'nn-club-kits__quote-remove';
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove ${line.label}`);
        remove.textContent = '×';
        remove.addEventListener('click', () => { state.designs.splice(index, 1); renderQuote(); });
        element.appendChild(remove);
        lines.appendChild(element);
      });
      const setupWaived = units > Number(config.setupWaiveAbove || 400);
      const setupTotal = setupWaived ? 0 : Number(config.setup.price || 0) * state.designs.length;
      const sampleTotal = Number(config.sample.price || 0) * state.designs.length;
      const fees = [
        { label: config.setup.label, detail: setupWaived ? `Waived above ${config.setupWaiveAbove} units` : `${state.designs.length} design${state.designs.length > 1 ? 's' : ''}`, amount: setupTotal },
        { label: config.sample.label, detail: `${state.designs.length} matched set${state.designs.length > 1 ? 's' : ''}`, amount: sampleTotal }
      ];
      fees.forEach((fee) => {
        const element = document.createElement('div');
        element.className = 'nn-club-kits__quote-line';
        element.innerHTML = `<span><b>${fee.label}</b><small>${fee.detail}</small></span><strong>${money(fee.amount)}</strong><span></span>`;
        lines.appendChild(element);
      });
      const total = productTotal + setupTotal + sampleTotal;
      const deposit = total * Number(config.depositPercent || 50) / 100;
      find('total').textContent = money(total);
      find('deposit').textContent = money(deposit);
      find('balance').textContent = money(total - deposit);
      find('per-unit').textContent = `${units} units across ${state.designs.length} design${state.designs.length > 1 ? 's' : ''} · ${money(total / Math.max(1, units))} average including setup and sample.`;
      find('quote-warning').textContent = setupWaived ? `Design setup is waived because the combined order exceeds ${config.setupWaiveAbove} units.` : '';
      const stored = { currency: config.currency, designs: state.designs, units, total, formattedTotal: money(total), setupWaived, summary: quoteSummary(total, units) };
      sessionStorage.setItem('nnClubKitsEstimate', JSON.stringify(stored));
    }

    function addDesign() {
      const item = product();
      const band = bandFor(item, state.quantity);
      const price = unitPrice(item, state.quantity);
      if (!band || price === null || state.quantity < item.moq) return;
      const chosenExtras = selectedExtras(item).map((extra) => extra.label);
      const quantity = state.quantity + state.goalkeeper;
      const details = [band.label, crest().label, ...chosenExtras, state.goalkeeper ? `${state.goalkeeper} GK` : ''].filter(Boolean).join(' · ');
      state.designs.push({ key: item.key, label: `${item.familyLabel} — ${item.productLabel}`, quantity, outfieldQuantity: state.quantity, goalkeeper: state.goalkeeper, unitPrice: price, details });
      renderQuote();
    }

    function renderAll() {
      renderTabs();
      renderOptions();
      find('quantity-range').value = state.quantity;
      find('quantity-number').value = state.quantity;
      find('gk-range').value = state.goalkeeper;
      find('gk-number').value = state.goalkeeper;
      renderPricing();
      renderCatalogue();
      renderQuote();
    }

    function resetPricing() {
      state.productIndex = 0;
      state.quantity = products[0]?.moq || 10;
      state.goalkeeper = 0;
      state.extras = {};
      state.crest = crests[0].key;
      state.designs = [];
      find('quantity-range').max = 500;
      find('quote-email').value = '';
      sessionStorage.removeItem('nnClubKitsEstimate');
      renderAll();
    }

    find('quantity-range').addEventListener('input', (event) => setQuantity(event.target.value));
    find('quantity-number').addEventListener('input', (event) => setQuantity(event.target.value));
    find('gk-range').addEventListener('input', (event) => { state.goalkeeper = Number(event.target.value); find('gk-number').value = state.goalkeeper; });
    find('gk-number').addEventListener('input', (event) => { state.goalkeeper = Math.max(0, Math.min(4, Number(event.target.value) || 0)); find('gk-range').value = state.goalkeeper; });
    find('crest').addEventListener('change', (event) => { state.crest = event.target.value; renderPricing(); });
    find('add-design').addEventListener('click', addDesign);
    find('reset-design').addEventListener('click', resetPricing);
    find('email-quote').addEventListener('click', () => {
      const email = find('quote-email');
      if (!email.checkValidity() || !state.designs.length) { email.reportValidity(); return; }
      const stored = JSON.parse(sessionStorage.getItem('nnClubKitsEstimate') || '{}');
      const subject = encodeURIComponent('Native Nature Club Kits planning estimate');
      const body = encodeURIComponent(`Native Nature Club Kits\n\n${stored.summary || ''}\n\nThis is a planning estimate and requires a formal quote.`);
      window.location.href = `mailto:${encodeURIComponent(email.value)}?subject=${subject}&body=${body}`;
    });

    if (!products.length) {
      find('add-design').disabled = true;
      find('reset-design').disabled = true;
      return;
    }
    state.quantity = Math.max(products[0].moq, 36);
    renderAll();
  }

  function initSizing(root) {
    const sizing = root.querySelector('[data-nn-sizing]');
    const configNode = root.querySelector('[data-nn-sizing-config]');
    if (!sizing || !configNode || sizing.dataset.nnReady === 'true') return;
    sizing.dataset.nnReady = 'true';

    let config;
    try { config = JSON.parse(configNode.textContent); } catch (error) {
      console.error('Club Kits sizing configuration is invalid.', error);
      return;
    }

    const state = { view: 'all', unit: 'cm' };
    const parseRows = (source) => String(source || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => line.split('|').map((cell) => cell.trim()));
    const convert = (value) => {
      if (state.unit === 'cm') return value;
      return String(value).replace(/\d+(?:\.\d+)?/g, (number) => String(Math.round((Number(number) / 2.54) * 10) / 10));
    };
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[character]));

    function table(chart, caption, rows, reference = false) {
      if (!rows.length) return '';
      const columns = chart.columns.map((column) => String(column).trim()).filter(Boolean);
      const head = columns.map((column) => `<th scope="col">${escapeHtml(column)}</th>`).join('');
      const body = rows.map((row) => {
        const cells = columns.map((_, index) => `<td>${escapeHtml(convert(row[index + 1] || '—'))}</td>`).join('');
        return `<tr><th scope="row">${escapeHtml(row[0] || '')}</th>${cells}</tr>`;
      }).join('');
      return `<div class="nn-club-kits__size-scroll"><table class="nn-club-kits__size-table${reference ? ' nn-club-kits__size-table--reference' : ''}"><caption>${escapeHtml(caption)}</caption><thead><tr><th scope="col">${escapeHtml(config.measurementHeading)}</th>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
    }

    function render() {
      const charts = state.view === 'all' ? config.charts : config.charts.filter((chart) => chart.key === state.view);
      sizing.querySelector('[data-nn-size-tables]').innerHTML = charts.map((chart) => {
        const unitName = state.unit === 'cm' ? 'centimetres' : 'inches';
        return `<section class="nn-club-kits__size-panel" data-size-panel="${escapeHtml(chart.key)}"><h2>${escapeHtml(chart.label)}</h2><p class="nn-club-kits__size-panel-intro">Use the shaded player row to choose a size. Garment measurements are shown flat in ${unitName}.</p>${table(chart, config.referenceCaption, parseRows(chart.referenceRows), true)}${table(chart, config.jerseyCaption, parseRows(chart.jerseyRows))}${table(chart, config.shortsCaption, parseRows(chart.shortsRows))}${chart.guidance ? `<div class="nn-club-kits__size-guidance">${chart.guidance}</div>` : ''}</section>`;
      }).join('');
    }

    sizing.querySelector('[data-nn-size-nav]').addEventListener('click', (event) => {
      const button = event.target.closest('[data-size-key]');
      if (!button) return;
      state.view = button.dataset.sizeKey;
      sizing.querySelectorAll('[data-size-key]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      render();
    });

    sizing.querySelectorAll('[data-size-unit]').forEach((button) => button.addEventListener('click', () => {
      state.unit = button.dataset.sizeUnit;
      sizing.querySelectorAll('[data-size-unit]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      render();
    }));

    render();
  }

  function initDesign(root) {
    const design = root.querySelector('[data-nn-design]');
    const configNode = root.querySelector('[data-nn-design-config]');
    const form = design?.querySelector('form');
    if (!design || !configNode || !form || design.dataset.nnReady === 'true') return;
    design.dataset.nnReady = 'true';

    let config;
    try { config = JSON.parse(configNode.textContent); } catch (error) {
      console.error('Club Kits design configuration is invalid.', error);
      return;
    }

    const steps = [...design.querySelectorAll('[data-wizard-step]')];
    const phases = [...design.querySelectorAll('[data-phase]')];
    const previous = design.querySelector('[data-nn-previous]');
    const next = design.querySelector('[data-nn-next]');
    const submit = design.querySelector('[data-nn-submit]');
    const controls = design.querySelector('[data-nn-wizard-controls]');
    const progress = design.querySelector('[data-nn-progress]');
    const success = design.querySelector('[data-nn-success]');
    let step = 0;
    let maxStep = 0;
    const escapeHtml = (input) => String(input ?? '').replace(/[&<>\"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '\"': '&quot;' }[character]));

    function value(name) {
      const field = form.elements.namedItem(name);
      return field && 'value' in field ? field.value : '';
    }

    function validateFiles() {
      const maxBytes = Number(config.maxFileMb || 10) * 1024 * 1024;
      for (const input of form.querySelectorAll('input[type="file"]')) {
        input.setCustomValidity('');
        const oversized = [...input.files].find((file) => file.size > maxBytes);
        if (oversized) {
          input.setCustomValidity(`${oversized.name} is larger than ${config.maxFileMb} MB.`);
          input.reportValidity();
          return false;
        }
      }
      return true;
    }

    function stepValid(index) {
      if (!steps[index]) return true;
      const required = [...steps[index].querySelectorAll('[required]')];
      const groups = {};
      for (const field of required) {
        if ((field.type === 'checkbox' || field.type === 'radio') && field.name) {
          groups[field.name] ||= [...steps[index].querySelectorAll('input[type="checkbox"], input[type="radio"]')].filter((item) => item.name === field.name);
        } else if (!field.checkValidity()) {
          field.reportValidity();
          return false;
        }
      }
      for (const fields of Object.values(groups)) {
        if (!fields.some((field) => field.checked)) {
          fields[0].setCustomValidity('Select at least one option.');
          fields[0].reportValidity();
          fields[0].setCustomValidity('');
          return false;
        }
      }
      return index !== 2 || validateFiles();
    }

    function quantityBand(units) {
      if (units <= 20) return '10–20';
      if (units <= 50) return '21–50';
      if (units <= 100) return '51–100';
      if (units <= 400) return '101–400';
      return '401+';
    }

    function estimate() {
      try { return JSON.parse(sessionStorage.getItem('nnClubKitsEstimate') || 'null'); } catch (_) { return null; }
    }

    function applyCarry() {
      const stored = estimate();
      const carry = design.querySelector('[data-nn-carry]');
      const hidden = design.querySelector('[data-nn-estimate-field]');
      if (!stored?.designs?.length) {
        carry.classList.remove('is-visible');
        carry.innerHTML = '';
        hidden.value = 'No planning estimate attached';
        return;
      }
      carry.classList.add('is-visible');
      carry.innerHTML = `<strong>Carried over from your estimate</strong>${stored.designs.map((line) => `<span class="nn-club-kits__carry-line"><span>${escapeHtml(line.label)}</span><span>${escapeHtml(line.quantity)} units</span></span>`).join('')}<span class="nn-club-kits__carry-line"><b>Estimated subtotal</b><b>${escapeHtml(stored.formattedTotal)}</b></span>`;
      hidden.value = stored.summary || JSON.stringify(stored);
      const quantity = design.querySelector('[data-nn-design-quantity]');
      if (quantity && !quantity.value) quantity.value = quantityBand(Number(stored.units || 0));
      const kits = [...form.querySelectorAll('[name="contact[Kit system]"]')];
      if (!kits.some((field) => field.checked)) {
        const home = kits.find((field) => /home/i.test(field.value));
        if (home) home.checked = true;
      }
    }

    function updateSummary() {
      const data = new FormData(form);
      const kits = data.getAll('contact[Kit system]').join(', ') || 'Not selected';
      const stored = estimate();
      design.querySelector('[data-nn-form-summary]').innerHTML = `<dl><dt>Club</dt><dd>${escapeHtml(value('contact[Club]') || '—')}</dd><dt>Contact</dt><dd>${escapeHtml(value('contact[Name]') || '—')} · ${escapeHtml(value('contact[email]') || '—')}</dd><dt>Kit system</dt><dd>${escapeHtml(kits)}</dd><dt>Quantity</dt><dd>${escapeHtml(value('contact[Quantity]') || '—')}</dd><dt>Colours</dt><dd>${escapeHtml(value('contact[Club colours]') || '—')}</dd><dt>Delivery target</dt><dd>${escapeHtml(value('contact[Preferred delivery date]') || '—')}</dd><dt>Estimate</dt><dd>${escapeHtml(stored?.formattedTotal || 'No estimate attached')}</dd></dl>`;
    }

    function renderStep() {
      steps.forEach((item, index) => item.classList.toggle('is-active', index === step));
      phases.forEach((phase, index) => {
        phase.setAttribute('aria-selected', String(index === step));
        phase.dataset.done = String(index < maxStep && index !== step);
        phase.dataset.locked = String(index > maxStep + 1);
      });
      progress.style.width = `${((step + 1) / steps.length) * 100}%`;
      previous.style.visibility = step === 0 ? 'hidden' : 'visible';
      next.hidden = step === steps.length - 1;
      submit.hidden = step !== steps.length - 1;
      if (step === 1) applyCarry();
      if (step === steps.length - 1) updateSummary();
    }

    function goStep(target) {
      if (target > step) {
        for (let index = step; index < target; index += 1) if (!stepValid(index)) return;
      }
      step = Math.max(0, Math.min(steps.length - 1, target));
      maxStep = Math.max(maxStep, step);
      renderStep();
      design.querySelector('[data-nn-phases]').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function showSuccess() {
      design.querySelector('[data-nn-wizard-steps]').hidden = true;
      controls.hidden = true;
      design.querySelector('[data-nn-phases]').hidden = true;
      design.querySelector('.nn-club-kits__progress').hidden = true;
      success.classList.add('is-visible');
      sessionStorage.removeItem('nnClubKitsEstimate');
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function showError(message) {
      let box = form.querySelector('.nn-club-kits__form-message--error');
      if (!box) {
        box = document.createElement('div');
        box.className = 'nn-club-kits__form-message nn-club-kits__form-message--error';
        box.setAttribute('role', 'alert');
        form.prepend(box);
      }
      box.textContent = message;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const boldness = design.querySelector('[data-nn-boldness]');
    const syncBoldness = () => {
      if (!boldness) return;
      boldness.querySelectorAll('label').forEach((label) => label.classList.toggle('is-selected', Boolean(label.querySelector('input:checked'))));
    };
    boldness?.addEventListener('change', syncBoldness);
    syncBoldness();

    phases.forEach((phase) => phase.addEventListener('click', () => {
      const target = Number(phase.dataset.phase);
      if (target <= maxStep + 1) goStep(target);
    }));
    previous.addEventListener('click', () => goStep(step - 1));
    next.addEventListener('click', () => goStep(step + 1));
    root.addEventListener('click', (event) => {
      if (event.target.closest('a[href="#nn-club-kits-design"]')) setTimeout(applyCarry, 0);
    });

    form.addEventListener('submit', async (event) => {
      if (!stepValid(step) || form.elements.namedItem('contact[Website]')?.value) {
        event.preventDefault();
        return;
      }
      updateSummary();
      if (config.mode !== 'endpoint') return;
      event.preventDefault();
      if (!config.endpoint || !/^https:\/\//i.test(config.endpoint)) {
        showError('A secure HTTPS submission endpoint has not been configured.');
        return;
      }
      submit.disabled = true;
      const original = submit.textContent;
      submit.textContent = 'Sending…';
      try {
        const response = await fetch(config.endpoint, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Submission returned ${response.status}`);
        form.reset();
        showSuccess();
      } catch (error) {
        console.error('Club Kits form submission failed.', error);
        showError(config.errorMessage);
      } finally {
        submit.disabled = false;
        submit.textContent = original;
      }
    });

    applyCarry();
    if (success.classList.contains('is-visible')) showSuccess();
    else renderStep();
  }

  function init(root) {
    if (root.dataset.nnShellReady !== 'true') {
      root.dataset.nnShellReady = 'true';
      initMenu(root);
    }
    initPricing(document);
    initSizing(document);
    initDesign(document);
  }

  document.querySelectorAll('[data-nn-club-kits]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    const shell = document.querySelector('[data-nn-club-kits]');
    if (shell) init(shell);
    initPricing(document);
    initSizing(document);
    initDesign(document);
  });
})();
